/**
 * @Copyright 2014-2019 DataVoke, Inc. All rights reserved.
 */

"use strict";

import { newUID } from "../utils/tools";
import { TransActionTypes } from "../utils/dvtypes";
import EventEmitter from '../support/eventemitter';

//#region Action Record Methods

/**
 * Method to undo the specified Action Record and callback if there's an error
 * @param {ActionRecord} actionRecord
 */
async function undoAction (actionRecord) {
    if (actionRecord.disposed) {
        throw new Error('Action Record has been disposed; cannot undo.');
    }

    switch (actionRecord.actionType) {
        case TransActionTypes.ValueChange:
        case TransActionTypes.AddItem:
        case TransActionTypes.DeleteItem:
        case TransActionTypes.Link:
        case TransActionTypes.Unlink:
            return new Promise((resolve, reject) => {
                /**
                 * We want the code that created the action record to do the actions to undo it. To do this,
                 * we want to callback the function that the transaction created.
                 *
                 *        actionContext = the "this" context we will pass to the apply function call.
                 *        undoFunction = the callback function
                 *        args = array of arguments to pass to the apply function call.
                 */

                // add our callback as the first parameter of the called back function that will
                // perform the predefined undo step.
                // NOTE: Since this is going to be called by the logic created for this action when completed,
                // the "resolve" is here instead of somewhere after the call below.
                actionRecord.args.splice(0, 0, () => {
                    resolve();
                });

                try {
                    // call the undo function for this action record step.
                    actionRecord.undoFunction.apply(actionRecord.actionContext, actionRecord.args);
                } catch (e) {
                    // return the error!
                    reject(e);
                }
            });

        case TransActionTypes.Import:
            throw new Error("Import transaction type is not currently supported.");

        default:
            // unknown type here..
            throw new Error("Unknown action record transaction type detected.");
    }
}

/**
 * Update the action transaction this action record belongs to
 * @param {ActionRecord} actionRecord - The action record instance to set transaction on
 * @param {ActionTransaction} trans - The transaction that holds this action record instance
 */
function setTransaction (actionRecord, trans) {
    if (actionRecord.disposed) {
        throw new Error('Action Record has been disposed; cannot set transaction.');
    }

    actionRecord._transaction = trans;
}

/**
 * Dispose references in this action record
 * @param {ActionRecord} actionRecord - The action record instance to dispose
 */
function disposeActionRecord (actionRecord) {
    if (actionRecord.disposed) {
        // only allow to dispose once.
        return;
    }

    actionRecord.disposed = true;

    actionRecord._transaction = null;
    actionRecord.undoFunction = null;
    actionRecord.args.length = 0;
    actionRecord.actionContext = null;
}

//#endregion

class ActionRecord {
    /**
     * Create a new instance of an action record
     * @param {TransActionTypes} actionType
     * @param {MVC} actionContext
     * @param {Function} undoFunction
     * @param {Array} args
     * @constructor
     */
    constructor (actionType, actionContext, undoFunction, args) {
        /**
         * Unique identifier for this action record instance
         * @type {string}
         */
        this.uid = newUID();

        switch (actionType) {
            case TransActionTypes.ValueChange:
            case TransActionTypes.AddItem:
            case TransActionTypes.DeleteItem:
            case TransActionTypes.Link:
            case TransActionTypes.Unlink:
                // these are all supported actions.
                break;

            case TransActionTypes.Import:
                throw new Error("Action type 'Import' not supported at this time.");

            default:
                throw new Error("Unknown transaction action type specified.");
        }

        /**
         * The type of action that was performed
         * @type {TransActionTypes}
         */
        this.actionType = actionType;

        /**
         * The action transaction context this action record belongs to
         * @type {ActionTransaction|null}
         * @protected
         */
        this._transaction = null;

        /**
         * The context that this action runs under (and will use for the undo)
         * @type {MVC}
         */
        this.actionContext = actionContext;

        /**
         * The logic to use to undo the changes made under this action
         * @type {Function}
         */
        this.undoFunction = undoFunction;

        /**
         * The arguments list to use with the undo function
         * @type {Array}
         */
        this.args = args || [];

        /**
         * Flag to indicate if this action record instance has been disposed
         * @type {boolean}
         */
        this.disposed = false;
    }

    /**
     * Attempt to undo this action
     */
    async undo () {
        return await undoAction(this);
    }

    /**
     * Update the action transaction this action record belongs to
     * @param {ActionTransaction} trans - The action transaction instance to assign this action record to
     */
    setTransaction (trans) {
        setTransaction(this, trans);
    }

    /**
     * Dispose references in this action record
     */
    dispose () {
        disposeActionRecord(this);
    }
}

//#region Action Transaction Methods

/**
 * Create a new Action Record using information specified, add it to this transaction, and return the new Action Record instance.
 * @param {ActionTransaction} trans - The action transaction that will hold the new action record
 * @param {TransActionTypes} actionType - The type of action to create and add
 * @param {MVC} actionContext - The MVC that this action runs against
 * @param {Function} undoFunction - The logic to undo the action
 * @param {Array} [args = []] - Arguments list to apply when undoing the action.
 * @return {ActionRecord} - The created and added Action Record instance
 */
function addActionRecord (trans, actionType, actionContext, undoFunction, args = []) {
    /**
     * Action Record instance using information specified
     * @type {ActionRecord}
     */
    let actionRecord = new ActionRecord(actionType, actionContext, undoFunction, args);

    if (actionRecord != null) {
        trans._actions.push(actionRecord);
        actionRecord.setTransaction(trans);
    }
    return actionRecord;
}

/**
 * Undo the actions, in reverse order they were added, in this transaction
 * @param {ActionTransaction} trans - The action transaction instance to undo
 * @returns {Promise<*>}
 */
async function undoTransaction (trans) {
    let errs = [];

    // loop backwards through the actions that were taken in this transaction
    for (let i = trans._actions.length - 1; i >= 0; i--) {
        let action = trans._actions[i];
        let err = await action.undo();
        if (err) {
            // only add the return object if there was one provided; nulls mean success
            errs.push(err);
        }
    }

    if (errs.length > 0) {
        return errs;
    }

    return null;
}

/**
 * Clear this action transaction of all action records.
 * @param {ActionTransaction} trans - The action transaction to dispose
 */
function disposeActionTransaction (trans) {
    if (trans.disposed) {
        // only allow disposing this instance once.
        return;
    }

    trans._actions.forEach((action) => {
        action.dispose();
    });
    trans._actions.length = 0;
    trans._actions = null;

    trans._monitor = null;
}

//#endregion

class ActionTransaction extends EventEmitter {
    /**
     * Create a new instance of an action transaction which holds and manages recorded actions
     * @param {string} actionName
     * @constructor
     */
    constructor (actionName) {
        super();

        /**
         * Unique identifier for this action transaction instance
         * @type {string}
         */
        this.uid = newUID();

        /**
         *
         * @type {string}
         */
        this.actionName = actionName;

        /**
         *
         * @type {Array}
         */
        this._actions = [];

        /**
         * The Action Monitor that created and controls this transaction
         * @type {ActionMonitor|null}
         */
        this._monitor = null;

        /**
         * Flag to indicate if this instance is disposed or not
         * @type {boolean}
         */
        this.disposed = false;
    }

    /**
     * Create a new Action Record using information specified, add it to this transaction, and return the new Action Record instance.
     * @param {TransActionTypes} actionType - The type of action to create and add
     * @param {MVC} actionContext - The MVC that this action runs against
     * @param {Function} undoFunction - The logic to undo the action
     * @param {Array} [args = []] - Arguments list to apply when undoing the action.
     * @return {ActionRecord} - The created and added Action Record instance
     */
    addAction (actionType, actionContext, undoFunction, args = []) {
        return addActionRecord(this, actionType, actionContext, undoFunction, args);
    }

    /**
     * Get the count of actions in this transaction
     * @returns {number}
     */
    get count () {
        return this._actions.length;
    }

    /**
     * Clear this action transaction of all action records.
     */
    dispose () {
        disposeActionTransaction(this);
    }

    /**
     * Set the parent monitor for this transaction
     * @param {ActionMonitor} monitor - The monitor to set as this transaction's parent monitor
     */
    setMonitor (monitor) {
        this._monitor = monitor;
    }

    /**
     * Undo the actions, in reverse order they were added, in this transaction
     * @returns {Array|null} - If if returns an array, they will be an array of errors for the undo steps
     */
    async undo () {
        return await undoTransaction(this);
    }
}

//#region Action Monitor Methods

/**
 * Begin a new transaction and make it the current transaction.
 * @param {ActionMonitor} monitor - The action monitor to hold the transaction
 * @param {string} transName - The name of the transaction to create
 * @return {ActionTransaction}
 */
function startTransaction (monitor, transName) {
    if (monitor.disposed) {
        throw new Error("Transaction monitor is disposed, cannot use.");
    }

    // if we already have an open transaction, use it.
    if (monitor._currentTransaction != null) {
        return monitor._currentTransaction;
    }

    // create a new transaction.
    monitor._currentTransaction = monitor._addTransaction(transName);
    return monitor._currentTransaction;
}

/**
 * Setting the internal current transaction object to null will prevent adding additional actions.
 * If the "dropTransactionFlag" is TRUE, then we don't want to keep this transaction. Default is FALSE.
 * NOTE: IF the current transaction has no records, it will be removed from the internal array anyway.
 * @param {ActionMonitor} monitor - The action monitor to stop the transaction on
 * @param {boolean} [discardCurrentTrans = false] - TRUE, discard current transaction; FALSE (DEFAULT), save current transaction if it has action records
 */
function stopTransaction (monitor, discardCurrentTrans = false) {
    if (discardCurrentTrans == null) {
        discardCurrentTrans = false;
    }

    // if no actions have been added to the transaction, remove it from our array.
    if (discardCurrentTrans ||
        (monitor._currentTransaction != null && monitor._currentTransaction.count === 0)) {
        // if there were no transaction action records or we are being told to
        // drop the current transaction without saving it, remove it from the array
        monitor._transactions.pop();

        // clear out the current transaction property.
        if (monitor._currentTransaction != null) {
            monitor._currentTransaction.dispose();
        }
    }

    monitor._currentTransaction = null;
}

/**
 * Create and add a new Action Transaction instance using the name specified
 * NOTE: This DOES NOT make the transaction the current transaction!
 * @param {ActionMonitor} monitor - The action monitor to add the new transaction to
 * @param {string} transName - The name to give the new Action Transaction instance
 * @return {ActionTransaction} - The created and added Action Transaction instance
 */
function addTransaction (monitor, transName) {
    if (monitor.disposed) {
        throw new Error("Transaction monitor is disposed, cannot use.");
    }

    // remove any transactions that puts us over our limit.
    while (monitor._transactions.length >= monitor.maxTransactions) {
        // remove the first (oldest) transaction from the array
        let oldTrans = monitor._transactions.shift();

        // dispose of the transaction removed.
        oldTrans.dispose();
    }

    let transaction = new ActionTransaction(transName);

    // put this transaction at the end (LIFO).
    monitor._transactions.push(transaction);
    transaction.setMonitor(monitor);

    return transaction;
}

/**
 * Create a new Action Record using information specified, add it to ths current transaction, and return the new Action Record instance.
 * NOTE: IF there is no current transaction, then no action will be created.
 * @param {ActionMonitor} monitor - The action monitor to add the new action record under it's current transaction
 * @param {TransActionTypes} actionType - The type of action to create and add
 * @param {MVC} actionContext - The MVC that this action runs against
 * @param {Function} undoFunction - The logic to undo the action
 * @param {Array} [args = []] - Arguments list to apply when undoing the action.
 * @return {ActionRecord} - The created and added Action Record instance
 */
function addActionRecordToCurrentTransaction (monitor, actionType, actionContext, undoFunction, args = []) {
    if (monitor.disposed) {
        throw new Error("Transaction monitor is disposed, cannot use.");
    }

    // if there is a current transaction, return the action created from it - otherwise it will return null
    return monitor._currentTransaction && monitor._currentTransaction.addAction(actionType, actionContext, undoFunction, args);
}

/**
 * Dispose of references within this instance
 * @param {ActionMonitor} monitor - The action monitor instance to dispose
 */
function disposeActionMonitor (monitor) {
    if (monitor.disposed) {
        // only allow object to be disposed once.
        return;
    }

    monitor.stopTransaction();

    clearActionMonitor(monitor);
    monitor._transactions = null;
    monitor._parentMvc = null;
    monitor.disposed = true;
}

/**
 * This function will rollback the specified transaction. If no transaction is specified,
 * then the last transaction will be rolled back. If there are no transactions in the
 * stack, then no action will be taken.
 * @param {ActionMonitor} monitor - The action monitor to undo the transaction in
 * @param {ActionTransaction|null} [trans = null] - The transaction to undo. Use last transaction if none specified
 */
async function monitorUndoTransaction (monitor, trans = null) {
    if (monitor.disposed) {
        callback(new Error("Transaction monitor is disposed, cannot use."));
        return;
    }

    // if no transaction was specified, get the last one in our list.
    if (trans == null) {
        if (monitor._currentTransaction != null) {
            // stop the current transaction and save it if possible.
            monitor.stopTransaction(false);
        }

        // get the last transaction that did something.
        trans = monitor._transactions.pop();
    }
    else if (monitor._currentTransaction != null && trans.uid === monitor._currentTransaction.uid) {
        // the current transaction is the one specified
        monitor.stopTransaction(false);

        trans = monitor._transactions.pop();
    }
    else {
        let index = monitor._transactions.indexOf(trans);
        if (index === -1) {
            // this transaction was not found here; cannot undo it.
            throw new Error('Transaction specified was not found for this Action Monitor.');
        }

        // remove this transaction from the list of transactions.
        monitor._transactions.splice(index, 1);
    }

    if (trans) {
        let errs = await trans.undo(),
            newError = null;

        if (Array.isArray(errs) && errs.length > 0) {
            // this will provide a simple Error object, or null if there were no errors.
            newError = new Error("Multiple errors found; please check errs[] off this error.");
            newError.errs = errs;

            throw newError;
        }

        trans.dispose();
    }
}

/**
 * Clear out all transactions (do not undo!) in the action monitor
 * @param {ActionMonitor} monitor - Reference to the action monitor instance
 */
function clearActionMonitor (monitor) {
    if (monitor.disposed) {
        return;
    }

    // ensure we stop the transaction
    monitor.stopTransaction();

    // clear out all transactions and dispose of each.
    monitor._transactions.forEach((trans) => {
        trans.dispose();
    });

    monitor._transactions.length = 0;
}

//#endregion

export class ActionMonitor extends EventEmitter {
    /**
     * Create a new instance of action monitor which monitors and controls transactions in LIFO order
     * @param {MVC} parentMvc
     * @constructor
     */
    constructor (parentMvc) {
        super();

        /**
         * Arbitrary limit for number of transaction to track
         * @type {number}
         * @private
         */
        this._limit = 20;

        /**
         * The UID of this action monitor
         * @type {string}
         */
        this.uid = newUID();

        /**
         * The mvc context for this monitor (should be one per MVC instance)
         * @type {MVC}
         * @protected
         */
        this._parentMvc = parentMvc;

        /**
         * Array of current transactions
         * @type {Array}
         * @protected
         */
        this._transactions = [];

        /**
         * Reference for current transaction
         * @type {ActionTransaction|null}
         * @protected
         */
        this._currentTransaction = null;

        /**
         * Flag to indicate if this action monitor instance has been disposed or not
         * @type {boolean}
         */
        this.disposed = false;
    }

    /**
     * Get the maximum number of transaction the monitor can keep track of
     * @returns {number}
     */
    get maxTransactions () {
        return this._limit;
    }

    /**
     * Return the current Action Transaction instance, if there is one
     * @return {ActionTransaction|null}
     */
    get currentTransaction () {
        return this._currentTransaction;
    }

    /**
     * Get the current count of transactions
     * @return {number}
     */
    get count () {
        return this._transactions.length;
    }

    /**
     * Begin a new transaction and make it the current transaction.
     * @param {string} nameTrans - The name of the transaction to create
     * @return {ActionTransaction}
     */
    startTransaction (nameTrans) {
        return startTransaction(this, nameTrans);
    }

    /**
     * Setting the internal current transaction object to null will prevent adding additional actions.
     * If the "dropTransactionFlag" is TRUE, then we don't want to keep this transaction. Default is FALSE.
     * NOTE: IF the current transaction has no records, it will be removed from the internal array anyway.
     * @param {boolean} [discardCurrentTrans = false] - TRUE, discard current transaction; FALSE (DEFAULT), save current transaction if it has action records
     */
    stopTransaction (discardCurrentTrans = false) {
        stopTransaction(this, discardCurrentTrans);
    }

    /**
     * Create and add a new Action Transaction instance using the name specified
     * NOTE: This DOES NOT make the transaction the current transaction!
     * @param {string} nameTrans - The name to give the new Action Transaction instance
     * @return {ActionTransaction} - The created and added Action Transaction instance
     * @protected
     */
    _addTransaction (nameTrans) {
        return addTransaction(this, nameTrans);
    }

    /**
     * Clear (do not undo) all transactions in the action monitor
     */
    clear () {
        clearActionMonitor(this);
    }

    /**
     * Create a new Action Record using information specified, add it to ths current transaction, and return the new Action Record instance.
     * NOTE: IF there is no current transaction, then no action will be created.
     * @param {TransActionTypes} actionType - The type of action to create and add
     * @param {MVC} actionContext - The MVC that this action runs against
     * @param {Function} undoFunction - The logic to undo the action
     * @param {Array} [args = []] - Arguments list to apply when undoing the action.
     * @return {ActionRecord} - The created and added Action Record instance
     */
    addAction (actionType, actionContext, undoFunction, args = []) {
        return addActionRecordToCurrentTransaction(this, actionType, actionContext, undoFunction, args);
    }

    /**
     * This function will rollback the specified transaction. If no transaction is specified,
     * then the last transaction will be rolled back. If there are no transactions in the
     * stack, then no action will be taken.
     * @param {ActionTransaction|null} [transaction = null] - The transaction to undo. Use last transaction if none specified
     */
    async undoTransaction (transaction) {
        await monitorUndoTransaction(this, transaction);
    }

    /**
     * Dispose of references within this instance
     */
    dispose () {
        disposeActionMonitor(this);
    }
}

/**
 * Create a new instance of Action Monitor for the specified MVC instance
 * @param {MVC} mvc - The MVC to create the action monitor instance for
 * @returns {ActionMonitor}
 */
export function create (mvc) {
    return new ActionMonitor(mvc);
}
